/**
 * Os BILHETES do documento — o trilho vertical de cada perna, o quarteto do
 * bilhete e o aviso de bilhetes separados.
 *
 * **Localizador POR BILHETE, nunca no cabeçalho** — com bilhetes separados cada um
 * tem o seu, e um código no topo mentiria para um deles.
 *
 * Mudança de dia mostra "chega ter, 25 ago" na linha da chegada, NUNCA "+1": a
 * marca só aparece quando a data muda, senão vira ruído.
 */
import { AirlineLogo } from '../../shared/airline_logo/airline_logo.js';
import type { VoucherDocumentLeg, VoucherDocumentTicket } from '../models.js';
import { VoucherEyebrow, VoucherFields } from './voucher_parts.js';

/** A marca do bilhete — a logo oficial da companhia; chip de texto como fallback
 *  quando o CDN não tem a companhia, e nada quando não há nem IATA nem nome. */
function TicketCarrier({ iata, name }: Readonly<{ iata?: string | null; name?: string | null }>) {
  // `wide`: o lockup horizontal, não o thumb quadrado — aqui o slot tem largura
  // livre e é a ALTURA de 16px que a linha do eyebrow fixa.
  if (iata) return <AirlineLogo iata={iata} name={name ?? undefined} size={16} shape="wide" />;
  if (!name) return null;
  return (
    <span className="shrink-0 rounded-sm bg-muted px-1.5 py-0.5 text-caption font-medium tracking-wide text-muted-foreground uppercase">
      {name}
    </span>
  );
}

/** Uma perna — trilho vertical (partida cheia, chegada vazada), pontas com hora +
 *  lugar, e o quarteto do bilhete embaixo quando o host o pendura aqui. */
function TicketLeg({ leg }: Readonly<{ leg: VoucherDocumentLeg }>) {
  return (
    <div className="flex gap-3">
      <div className="flex w-3 shrink-0 flex-col items-center gap-1 self-stretch">
        <span className="mt-1.5 size-1.5 shrink-0 rounded-full bg-foreground" />
        <span className="w-px flex-1 bg-border" />
        <span className="mb-1.5 size-1.5 shrink-0 rounded-full border border-foreground bg-background" />
      </div>
      <div className="grid min-w-0 flex-1 gap-1.5">
        <p className="text-title text-foreground">
          <span className="tabular-nums">{leg.departTime}</span> {leg.departPlace}
        </p>
        {leg.meta ? <p className="text-detail text-muted-foreground">{leg.meta}</p> : null}
        <div className="flex items-baseline justify-between gap-2">
          <p className="min-w-0 text-title text-foreground">
            <span className="tabular-nums">{leg.arriveTime}</span> {leg.arrivePlace}
          </p>
          {leg.arrivalNote ? (
            <p className="shrink-0 text-ui font-medium text-destructive">{leg.arrivalNote}</p>
          ) : null}
        </div>
        {leg.fields && leg.fields.length > 0 ? (
          <div className="mt-2">
            <VoucherFields fields={leg.fields} />
          </div>
        ) : null}
      </div>
    </div>
  );
}

function Ticket({ ticket }: Readonly<{ ticket: VoucherDocumentTicket }>) {
  return (
    <div className="grid gap-5">
      <div className="flex items-center justify-between gap-3">
        <VoucherEyebrow>{ticket.eyebrow}</VoucherEyebrow>
        <TicketCarrier iata={ticket.carrierIata} name={ticket.carrierName} />
      </div>
      {ticket.legs.map((leg) => (
        <div key={leg.key} className="grid gap-5">
          <TicketLeg leg={leg} />
          {leg.connection ? (
            <p className="text-detail text-muted-foreground">{leg.connection}</p>
          ) : null}
        </div>
      ))}
    </div>
  );
}

/** Os bilhetes empilhados, com o aviso de bilhetes separados ONDE o localizador
 *  troca e a nota de fuso no pé. */
export function VoucherTickets({
  tickets,
  footnote,
}: Readonly<{ tickets: VoucherDocumentTicket[]; footnote?: string | null }>) {
  return (
    <div className="grid gap-5">
      {tickets.map((ticket) => (
        <div key={ticket.key} className="grid gap-5">
          <Ticket ticket={ticket} />
          {ticket.alert ? (
            <div className="grid gap-1 rounded-lg bg-warning-soft p-3">
              <p className="text-detail font-medium text-foreground">{ticket.alert.title}</p>
              <p className="text-detail text-foreground">{ticket.alert.text}</p>
            </div>
          ) : null}
        </div>
      ))}
      {footnote ? <p className="text-detail text-muted-foreground">{footnote}</p> : null}
    </div>
  );
}
